import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ServiceLocatorService } from '@ncs/ncs-lib';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface TaskEditModalData {
  task: {
    id: string;
    title: string;
    description: string;
    status: string;
    dueDate?: Date;
    priority: 'low' | 'medium' | 'high' | 'critical';
    assignee?: string;
    labels?: string[];
    createdAt: Date;
    updatedAt: Date;
    order: number;
  };
  isNewTask: boolean;
  /** Application ID — used to scope the assignee list */
  appId?: number;
}

@Component({
  selector: 'app-task-edit-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './task-edit-modal.component.html',
  styleUrls: ['./task-edit-modal.component.scss'],
})
export class TaskEditModalComponent implements OnInit {
  taskForm!: FormGroup;
  priorityOptions = [
    { value: 'low', label: 'Low', color: '#7ED321' },
    { value: 'medium', label: 'Medium', color: '#F5A623' },
    { value: 'high', label: 'High', color: '#FF6B6B' },
    { value: 'critical', label: 'Critical', color: '#9013FE' },
  ];

  /** Full list of application-scoped users fetched from the backend */
  appUsers: any[] = [];
  /** Subset shown in the dropdown after typing in the search box */
  filteredUsers: any[] = [];
  /** Text typed into the user-search box */
  assigneeSearch = '';
  /** Controls dropdown visibility */
  showAssigneeDropdown = false;
  /** Display name of the currently selected assignee */
  selectedAssigneeName = '';

  isLoadingUsers = false;

  constructor(
    public dialogRef: MatDialogRef<TaskEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TaskEditModalData,
    private fb: FormBuilder,
    private locator: ServiceLocatorService,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadAppUsers();
    // Seed the display name if a task already has an assignee
    if (this.data.task.assignee) {
      this.selectedAssigneeName = this.data.task.assignee;
      this.assigneeSearch = this.data.task.assignee;
    }
  }

  private initializeForm(): void {
    this.taskForm = this.fb.group({
      title: [
        this.data.task.title || '',
        [Validators.required, Validators.minLength(3)],
      ],
      description: [this.data.task.description || ''],
      priority: [this.data.task.priority || 'medium'],
      dueDate: [this.data.task.dueDate || null],
      assignee: [this.data.task.assignee || ''],
      labelsInput: [
        this.data.task.labels ? this.data.task.labels.join(', ') : '',
      ],
    });
  }

  // ─── Assignee user loading (application-scoped) ──────────────────────────

  /**
   * Loads accepted users for the current application.
   * Reuses the GET /ctl/task/app-users/{appId} endpoint added in TasksCtl.
   */
  private loadAppUsers(): void {
    const appId = this.data.appId;
    if (!appId || appId <= 0) {
      // No appId context — leave list empty; assignee field stays free-text
      return;
    }

    this.isLoadingUsers = true;
    this.locator.config
      .getProperty('endpoint.task')
      .then((taskEndpoint: string) => {
        const url = `${taskEndpoint}/app-users/${appId}`;
        this.locator.http.get(url, (res: any, error: boolean) => {
          this.isLoadingUsers = false;
          if (error || !res?.success) {
            console.warn('Could not load app users for assignee list', error);
            return;
          }
          this.appUsers = (res.result?.data || []).map((u: any) => ({
            ...u,
            userFirstname: u.userFirstname?.trim() || u.email || 'Unknown',
          }));
          this.filteredUsers = [...this.appUsers];
        });
      });
  }

  // ─── Assignee search / selection ─────────────────────────────────────────

  onAssigneeSearchChange(value: string): void {
    this.assigneeSearch = value;
    const q = value.toLowerCase().trim();
    this.filteredUsers = q
      ? this.appUsers.filter((u) =>
          (u.userFirstname || u.email || '').toLowerCase().includes(q),
        )
      : [...this.appUsers];
    this.showAssigneeDropdown = true;
    // If user cleared the box, also clear the form control
    if (!value.trim()) {
      this.taskForm.get('assignee')?.setValue('');
      this.selectedAssigneeName = '';
    }
  }

  selectAssignee(user: any): void {
    const name: string = user.userFirstname || '';
    this.taskForm.get('assignee')?.setValue(name);
    this.selectedAssigneeName = name;
    this.assigneeSearch = name;
    this.showAssigneeDropdown = false;
  }

  openAssigneeDropdown(): void {
    this.filteredUsers = [...this.appUsers];
    this.showAssigneeDropdown = true;
  }

  closeAssigneeDropdown(): void {
    // Slight delay so click on an option registers before the blur fires
    setTimeout(() => {
      this.showAssigneeDropdown = false;
    }, 200);
  }

  clearAssignee(): void {
    this.taskForm.get('assignee')?.setValue('');
    this.selectedAssigneeName = '';
    this.assigneeSearch = '';
    this.filteredUsers = [...this.appUsers];
  }

  // ─── Save / Cancel ───────────────────────────────────────────────────────

  onSave(): void {
    if (this.taskForm.valid) {
      const formValue = this.taskForm.value;
      const updatedTask = {
        ...this.data.task,
        title: formValue.title,
        description: formValue.description,
        priority: formValue.priority,
        dueDate: formValue.dueDate,
        assignee: formValue.assignee,
        labels: formValue.labelsInput
          ? formValue.labelsInput
              .split(',')
              .map((label: string) => label.trim())
              .filter((label: string) => label)
          : [],
      };
      this.dialogRef.close(updatedTask);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getPriorityColor(priority: string): string {
    const priorityObj = this.priorityOptions.find((p) => p.value === priority);
    return priorityObj ? priorityObj.color : '#F5A623';
  }
}
